<script setup>
// A JANELA DE 24 H DO WHATSAPP OFICIAL, acima do campo de resposta (24/09/2026).
//
// Caso que motivou: "TESTE" ao Eduardo às 13:45 mostrou ✓ no advup e nunca
// chegou — a Meta recusa mensagem livre fora da janela (erro 131047) e ninguém
// via. Dois estados, pela escada de gravidade do produto:
// - ÂMBAR, faltando menos de 4 h: a contagem e uma barra do que sobra;
// - RUBI, fechada: quando o cliente escreveu, a regra em uma frase e o que DÁ
//   para fazer (a janela reabre quando o cliente responde ou liga; nota
//   interna). O campo trava junto. Sem sugerir o celular do escritório
//   (Victor, 24/09).
// A conta mora em helper/janelaDeResposta.js; o relógio, em useJanelaDeResposta.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, messageStamp } from 'shared/helpers/timeHelper';
import { partesDoRestante } from 'dashboard/helper/janelaDeResposta';

const props = defineProps({
  janela: { type: Object, required: true },
  nome: { type: String, default: '' },
  // Lead de anúncio (22/10/2026): até quando modelo aprovado sai grátis, em
  // segundos (`anuncio_gratis_ate` da conversa, FollowUpCadences::JanelaDoAnuncio).
  anuncioGratisAte: { type: Number, default: 0 },
});

defineEmits(['nota', 'modelo']);

const { t } = useI18n();

// Nome do WhatsApp sem nenhuma letra ("¥", emoji) não é nome: vira "O cliente".
const primeiroNome = computed(() => {
  const primeiro = (props.nome || '').trim().split(/\s+/)[0];
  return /\p{L}/u.test(primeiro || '')
    ? primeiro
    : t('CONVERSATION.JANELA.CLIENTE');
});

// A janela fechada não libera texto livre, mas o modelo sai de graça até aqui.
const gratisAte = computed(() => {
  const ate = props.anuncioGratisAte || 0;
  if (ate * 1000 <= Date.now()) return '';
  return messageStamp(ate, "EEE, dd/MM 'às' HH:mm");
});

const tempoRestante = computed(() => {
  const { horas, minutos } = partesDoRestante(props.janela.restante);
  if (horas > 0)
    return t('CONVERSATION.JANELA.HORAS_MINUTOS', { horas, minutos });
  if (minutos > 0) return t('CONVERSATION.JANELA.MINUTOS', { minutos });
  return t('CONVERSATION.JANELA.MENOS_DE_UM_MINUTO');
});

const ultimaSegundos = computed(() =>
  props.janela.ultimaDoCliente
    ? Math.floor(props.janela.ultimaDoCliente / 1000)
    : 0
);

const quandoEscreveu = computed(() => {
  if (!ultimaSegundos.value) return '';
  return t('CONVERSATION.JANELA.QUANDO', {
    relativo: dynamicTime(ultimaSegundos.value),
    data: messageStamp(ultimaSegundos.value, "dd/MM 'às' HH:mm"),
  });
});

// A barra encolhe da direita para a esquerda: o que sobra das 24 h.
const estiloDaBarra = computed(() => ({
  '--janela-fracao': Math.max(props.janela.fracao, 0.02),
}));
</script>

<template>
  <div
    v-if="janela.estado === 'acabando'"
    class="rb-janela rb-janela--acabando"
    role="status"
    :style="estiloDaBarra"
  >
    <span class="i-hugeicons-hourglass rb-janela__ico" aria-hidden="true" />
    <p class="rb-janela__linha">
      <strong class="rb-janela__forte">
        {{ $t('CONVERSATION.JANELA.ACABANDO', { tempo: tempoRestante }) }}
      </strong>
      <span class="rb-janela__depois">
        {{ $t('CONVERSATION.JANELA.ACABANDO_DEPOIS') }}
      </span>
    </p>
    <span class="rb-janela__barra" aria-hidden="true" />
  </div>

  <section
    v-else-if="janela.estado === 'fechada'"
    class="rb-janela rb-janela--fechada"
    role="status"
    aria-live="polite"
  >
    <header class="rb-janela__cab">
      <span class="advup-card__selo rb-janela__selo" aria-hidden="true">
        <i class="i-ph-lock-simple-fill" />
      </span>
      <div class="rb-janela__titulos">
        <h3 class="rb-janela__tit">{{ $t('CONVERSATION.JANELA.FECHADA') }}</h3>
        <p class="rb-janela__sub">
          <template v-if="janela.nuncaEscreveu">
            {{ $t('CONVERSATION.JANELA.NUNCA', { nome: primeiroNome }) }}
          </template>
          <template v-else>
            {{
              $t('CONVERSATION.JANELA.ESCREVEU', {
                nome: primeiroNome,
                quando: quandoEscreveu,
              })
            }}
          </template>
        </p>
      </div>
    </header>

    <p class="rb-janela__regra rb-janela__so-largo">
      {{ $t('CONVERSATION.JANELA.REGRA') }}
    </p>

    <ul class="rb-janela__caminhos">
      <li v-if="gratisAte" class="rb-janela__caminho">
        <span
          class="i-ph-megaphone-simple rb-janela__caminho-ico"
          aria-hidden="true"
        />
        <span>
          <strong class="rb-janela__forte">
            {{ $t('CONVERSATION.JANELA.ANUNCIO_GRATIS') }}
          </strong>
          {{
            $t('CONVERSATION.JANELA.ANUNCIO_GRATIS_NOTA', { quando: gratisAte })
          }}
        </span>
      </li>
      <li class="rb-janela__caminho">
        <span
          class="i-hugeicons-arrow-reload-horizontal rb-janela__caminho-ico"
          aria-hidden="true"
        />
        <span>
          <strong class="rb-janela__forte">
            {{ $t('CONVERSATION.JANELA.REABRE') }}
          </strong>
          {{ $t('CONVERSATION.JANELA.REABRE_NOTA') }}
        </span>
      </li>
    </ul>

    <div class="rb-janela__acoes">
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="$emit('nota')"
      >
        <i class="i-hugeicons-note-edit" aria-hidden="true" />
        {{ $t('CONVERSATION.JANELA.NOTA_INTERNA') }}
      </button>
      <!-- A saída para a janela fechada (25/09/2026): modelo aprovado chega. -->
      <button
        type="button"
        class="advup-btn-forte advup-btn--compacto"
        @click="$emit('modelo')"
      >
        <i class="i-ph-paper-plane-tilt-fill" aria-hidden="true" />
        {{ $t('CONVERSATION.JANELA.ENVIAR_MODELO') }}
      </button>
    </div>
  </section>
</template>

<style scoped>
.rb-janela {
  position: relative;
  overflow: hidden;
  margin-bottom: var(--esp-2);
  border: 1px solid var(--janela-borda);
  border-radius: var(--r-3);
  background: var(--janela-fundo);
  color: var(--janela-tinta);
}

/* ── ÂMBAR: faltando menos de 4 h ─────────────────────────────────────────── */
.rb-janela--acabando {
  --janela-borda: rgb(var(--amber-6));
  --janela-fundo: rgb(var(--amber-2));
  --janela-tinta: rgb(var(--amber-11));
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  /* 3px embaixo a mais: é o trilho da barra */
  padding: var(--esp-2) var(--esp-3) calc(var(--esp-2) + 3px);
}

.rb-janela__ico {
  flex-shrink: 0;
  width: var(--ico-2);
  height: var(--ico-2);
}

.rb-janela__linha {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
}

.rb-janela__forte {
  font-weight: var(--peso-forte);
}

.rb-janela__depois {
  color: rgb(var(--slate-11));
}

.rb-janela__depois::before {
  content: ' · ';
}

/* A barra do que SOBRA das 24 h: encolhe para a esquerda conforme o prazo
   corre. Transformação, não largura — anima sem recalcular o layout. */
.rb-janela__barra {
  position: absolute;
  inset: auto 0 0;
  height: 3px;
  background: rgb(var(--amber-9));
  transform: scaleX(var(--janela-fracao, 1));
  transform-origin: left;
  transition: transform var(--mov-4) var(--mov-curva);
}

/* ── RUBI: fechada ────────────────────────────────────────────────────────── */
.rb-janela--fechada {
  --janela-borda: rgb(var(--ruby-6));
  --janela-fundo: rgb(var(--ruby-2));
  --janela-tinta: rgb(var(--slate-12));
  --card-forte: rgb(var(--ruby-9));
  display: grid;
  gap: var(--esp-3);
  padding: var(--esp-4);
  /* O véu que escorre do canto do cadeado e morre antes da metade: superfície
     grande chapada lê como preenchimento (padrão, "Ambição" 1). */
  background-image: radial-gradient(
    circle at 0 0,
    rgb(var(--ruby-9) / 10%),
    transparent 55%
  );
}

.rb-janela__cab {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
}

.rb-janela__titulos {
  min-width: 0;
}

.rb-janela__tit {
  margin: 0;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--ruby-11));
}

.rb-janela__sub {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}

.rb-janela__regra {
  margin: 0;
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  letter-spacing: var(--rastro-corpo);
}

.rb-janela__caminhos {
  display: grid;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  list-style: none;
  border-radius: var(--r-3);
  background: rgb(var(--solid-1) / 70%);
}

.rb-janela__caminho {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.rb-janela__caminho .rb-janela__forte {
  color: rgb(var(--slate-12));
}

.rb-janela__caminho-ico {
  flex-shrink: 0;
  width: var(--ico-2);
  height: var(--ico-2);
  /* centro óptico com a PRIMEIRA linha (12px × 1,6 = ~19px de linha, ícone de 16) */
  translate: 0 0.125em;
  color: rgb(var(--slate-11));
}

.rb-janela__acoes {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--esp-2);
}

/* No celular o cartão inteiro cobria a conversa (medido em 375px: 395px de
   altura). Fica o essencial — fechada, desde quando, quando reabre e a nota; a
   regra por extenso é leitura de tela larga. O botão vira alvo de toque inteiro. */
@media (max-width: 639px) {
  .rb-janela--fechada {
    gap: var(--esp-2);
    padding: var(--esp-3);
  }

  .rb-janela__so-largo {
    display: none;
  }

  .rb-janela__caminhos {
    padding: var(--esp-2) var(--esp-3);
  }

  .rb-janela__acoes > .advup-btn,
  .rb-janela__acoes > .advup-btn-forte {
    width: 100%;
    height: 44px;
  }

  .rb-janela__depois {
    display: block;
  }

  .rb-janela__depois::before {
    content: none;
  }
}
</style>
